import React, { useCallback, useMemo, useRef, useState } from "react";
import TableContainer from "components/common/TableContainer";
import { Link } from "react-router-dom";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import {
  deleteEnquirySiteVisit,
  getEnquirySiteVisits,
} from "features/EnquirySiteVisits/services/EnquirySiteVisitService";
import type { EnquirySiteVisit } from "features/EnquirySiteVisits/types/enquiry-site-visit.types";
import {
  EnquirySiteVisitPriorityMap,
  EnquirySiteVisitStatus,
  EnquirySiteVisitStatusMap,
} from "features/EnquirySiteVisits/constants/enquiry-site-visit.constants";
import DeleteModal from "components/common/DeleteModal";
import { toast } from "react-toastify";
import { ColumnDef } from "@tanstack/react-table";
import { CodeCell, IndexColumn } from "components/common/columns";
import { DateTime } from "components/common/Date";
import StatusBadge from "components/common/StatusBadge";
import { runEnquirySiteVisitStart } from "features/EnquirySiteVisits/enquirySiteVisitWorkflow";
import {
  getSiteVisitWorkflowUiVisibility,
  guardSiteVisitCancel,
  guardSiteVisitComplete,
  guardSiteVisitStart,
} from "features/EnquirySiteVisits/siteVisitWorkflowAccess";
import EnquirySiteVisitWorkflowModals from "features/EnquirySiteVisits/EnquirySiteVisitWorkflowModals";
import WorkAssignmentForm from "features/WorkAssignments/WorkAssignmentForm";
import { WorkAssignmentSourceType } from "features/WorkAssignments/constants/work-assignment.constants";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import {
  canReassignSiteVisit as isSiteVisitReassignable,
  shouldShowSiteVisitActionButtons,
} from "features/EnquirySiteVisits/utils/siteVisitActionPolicy";
import { hasAnyEnquirySiteVisitEditPermission } from "features/EnquirySiteVisits/utils/enquirySiteVisitEditFieldPolicy";
import { useProfile } from "features/Authentication/hooks/useProfile";
import EnquirySiteVisitListFilters from "./EnquirySiteVisitListFilters";
import SiteVisitAssigneesAvatarGroup from "./SiteVisitAssigneesAvatarGroup";

/** Delete is blocked only while the visit is active (IN_PROGRESS) or done (COMPLETED). */
function canDeleteSiteVisit(status: string | null | undefined): boolean {
  const st = String(status ?? "").toUpperCase();
  return st !== EnquirySiteVisitStatus.IN_PROGRESS && st !== EnquirySiteVisitStatus.COMPLETED;
}


type SiteVisitListRowActionsCellProps = {
  record: EnquirySiteVisit;
  isEmployee: boolean;
  canAssignSiteVisit: boolean;
  canReassignSiteVisit: boolean;
  busyVisitId: number | null;
  onStartVisit: (r: EnquirySiteVisit) => void;
  onOpenComplete: (r: EnquirySiteVisit) => void;
  onOpenCancel: (r: EnquirySiteVisit) => void;
  onOpenAssign: (r: EnquirySiteVisit) => void;
  onOpenReassign: (r: EnquirySiteVisit) => void;
  onDelete: (r: EnquirySiteVisit) => void;
};

function SiteVisitListRowActionsCell({
  record,
  isEmployee,
  canAssignSiteVisit,
  canReassignSiteVisit,
  busyVisitId,
  onStartVisit,
  onOpenComplete,
  onOpenCancel,
  onOpenAssign,
  onOpenReassign,
  onDelete,
}: SiteVisitListRowActionsCellProps) {
  const isActionable = shouldShowSiteVisitActionButtons(record.status);
  const canEditVisit = hasAnyEnquirySiteVisitEditPermission(record.status);
  const canDelete = canDeleteSiteVisit(record.status);
  const { showStart, showComplete, showCancel, showAnyWorkflow } =
    getSiteVisitWorkflowUiVisibility(record.status, isEmployee);
  const showWorkflowItems = isActionable && showAnyWorkflow;
  const showAssignmentItems = isActionable && (canAssignSiteVisit || canReassignSiteVisit);
  const rowBusy = busyVisitId === record.id;

  return (
    <UncontrolledDropdown>
      <DropdownToggle
        type="button"
        className="btn btn-soft-secondary btn-sm"
        tag="button"
        aria-label="Site visit actions"
      >
        <i className="ri-more-2-fill align-middle" />
      </DropdownToggle>
      <DropdownMenu container="body" end>
        <DropdownItem
          tag={Link}
          to={`/enquiry-management/site-visits/${record.id}`}
        >
          <i className="ri-eye-line align-bottom me-2 text-muted" />
          View
        </DropdownItem>
        {canEditVisit ? (
          <DropdownItem
            tag={Link}
            to={`/enquiry-management/site-visits/edit/${record.id}`}
          >
            <i className="ri-pencil-line align-bottom me-2 text-muted" />
            Edit
          </DropdownItem>
        ) : null}
        {showAssignmentItems ? (
          <>
            <DropdownItem divider />
            {canAssignSiteVisit ? (
              <DropdownItem
                disabled={rowBusy}
                onClick={() => onOpenAssign(record)}
              >
                <i className="ri-user-follow-line align-bottom me-2 text-muted" />
                Assign user
              </DropdownItem>
            ) : null}
            {canReassignSiteVisit ? (
              <DropdownItem
                disabled={rowBusy}
                onClick={() => void onOpenReassign(record)}
              >
                <i className="ri-user-shared-line align-bottom me-2 text-muted" />
                Reassign
              </DropdownItem>
            ) : null}
          </>
        ) : null}
        {showWorkflowItems ? (
          <>
            <DropdownItem divider />
            {showStart ? (
              <DropdownItem
                disabled={rowBusy}
                onClick={() => void onStartVisit(record)}
              >
                <i className="ri-play-circle-line align-bottom me-2 text-primary" />
                Start visit
              </DropdownItem>
            ) : null}
            {showComplete ? (
              <DropdownItem
                disabled={rowBusy}
                onClick={() => void onOpenComplete(record)}
              >
                <i className="ri-checkbox-circle-line align-bottom me-2 text-success" />
                Complete visit
              </DropdownItem>
            ) : null}
            {showCancel ? (
              <DropdownItem
                disabled={rowBusy}
                onClick={() => onOpenCancel(record)}
              >
                <i className="ri-close-circle-line align-bottom me-2 text-warning" />
                Cancel visit
              </DropdownItem>
            ) : null}
          </>
        ) : null}
        {canDelete ? (
          <>
            <DropdownItem divider />
            <DropdownItem
              className="text-danger"
              onClick={() => onDelete(record)}
            >
              <i className="ri-delete-bin-line align-bottom me-2" />
              Delete
            </DropdownItem>
          </>
        ) : null}
      </DropdownMenu>
    </UncontrolledDropdown>
  );
}

export const EnquirySiteVisitsTable = () => {
  const tableRef = useRef<() => void>(null);
  const { isEmployee } = useProfile();
  const { can } = useAccess();
  const canAssignSiteVisit = useMemo(
    () => can(Permissions.WORK_ASSIGNMENT.CREATE),
    [can],
  );
  const canReassignSiteVisit = useMemo(
    () => can(Permissions.WORK_ASSIGNMENT.UPDATE),
    [can],
  );
  const canReadSiteVisit = useMemo(
    () => can(Permissions.SITE_VISIT.READ),
    [can],
  );
  const [visitToDelete, setVisitToDelete] = useState<EnquirySiteVisit>();
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [workflowModalVisit, setWorkflowModalVisit] =
    useState<EnquirySiteVisit | null>(null);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [busyVisitId, setBusyVisitId] = useState<number | null>(null);
  const [assignFormOpen, setAssignFormOpen] = useState(false);
  const [assignFormVisit, setAssignFormVisit] =
    useState<EnquirySiteVisit | null>(null);
  const [assignFormIsReassign, setAssignFormIsReassign] = useState(false);

  const [selectedStatuses, setSelectedStatuses] = useState<
    EnquirySiteVisitStatus[]
  >([]);
  const [enquiryIdFilter, setEnquiryIdFilter] = useState<number | null>(null);
  const [clientIdFilter, setClientIdFilter] = useState<number | null>(null);

  const toggleStatus = useCallback(
    (st: EnquirySiteVisitStatus, checked: boolean) => {
      setSelectedStatuses((prev) => {
        if (checked) return prev.includes(st) ? prev : [...prev, st];
        return prev.filter((x) => x !== st);
      });
    },
    [],
  );

  const requestParams = useMemo(
    () => ({
      status:
        selectedStatuses.length > 0 ? selectedStatuses.join(",") : undefined,
      enquiryId: enquiryIdFilter ?? undefined,
      clientId: clientIdFilter ?? undefined,
    }),
    [clientIdFilter, enquiryIdFilter, selectedStatuses],
  );

  const filtersBadgeCount = useMemo(() => {
    let n = 0;
    if (selectedStatuses.length > 0) n += 1;
    if (enquiryIdFilter != null) n += 1;
    if (clientIdFilter != null) n += 1;
    return n;
  }, [clientIdFilter, enquiryIdFilter, selectedStatuses.length]);

  const clearListFilters = useCallback(() => {
    setSelectedStatuses([]);
    setEnquiryIdFilter(null);
    setClientIdFilter(null);
  }, []);

  const refreshTable = useCallback(() => {
    tableRef.current?.();
  }, []);

  const onClickDelete = useCallback((record: EnquirySiteVisit) => {
    setVisitToDelete(record);
    setDeleteModal(true);
  }, []);

  const handleStartVisit = useCallback(
    async (record: EnquirySiteVisit) => {
      if (!guardSiteVisitStart(record, isEmployee)) return;
      setBusyVisitId(record.id);
      try {
        const ok = await runEnquirySiteVisitStart(record.id);
        if (ok) refreshTable();
      } finally {
        setBusyVisitId(null);
      }
    },
    [refreshTable, isEmployee],
  );

  const openCompleteModal = useCallback(
    (record: EnquirySiteVisit) => {
      if (!guardSiteVisitComplete(record, isEmployee)) return;
      setWorkflowModalVisit(record);
      setCompleteOpen(true);
    },
    [isEmployee],
  );

  const openCancelModal = useCallback(
    (record: EnquirySiteVisit) => {
      if (!guardSiteVisitCancel(record.status, isEmployee)) return;
      setWorkflowModalVisit(record);
      setCancelOpen(true);
    },
    [isEmployee],
  );

  const closeCompleteModal = useCallback(() => {
    setCompleteOpen(false);
    setWorkflowModalVisit(null);
  }, []);

  const closeCancelModal = useCallback(() => {
    setCancelOpen(false);
    setWorkflowModalVisit(null);
  }, []);

  const closeAssignmentForm = useCallback(() => {
    setAssignFormOpen(false);
    setAssignFormVisit(null);
    setAssignFormIsReassign(false);
  }, []);

  const openAssign = useCallback((record: EnquirySiteVisit) => {
    setAssignFormIsReassign(false);
    setAssignFormVisit(record);
    setAssignFormOpen(true);
  }, []);

  const openReassign = useCallback((record: EnquirySiteVisit) => {
    if (!isSiteVisitReassignable(record.status)) {
      toast.info("Completed or cancelled site visits cannot be reassigned.");
      return;
    }
    setAssignFormIsReassign(true);
    setAssignFormVisit(record);
    setAssignFormOpen(true);
  }, []);

  const handleDeleteEnquirySiteVisit = async () => {
    if (visitToDelete) {
      try {
        const response = await deleteEnquirySiteVisit(visitToDelete.id);
        if (response.success) {
          toast.success(response.message);
          tableRef.current?.();
        }
        setDeleteModal(false);
      } catch (err) {
        console.warn("Error deleting record: ", err);
        toast.error(
          (err as any)?.response?.data?.message ??
            (err as any)?.message ??
            "Could not delete site visit",
        );
      }
    }
  };

  const columns: ColumnDef<EnquirySiteVisit>[] = useMemo(
    () => [
      IndexColumn<EnquirySiteVisit>(),
      {
        header: "Code",
        accessorKey: "code",
        enableColumnFilter: false,
        cell: ({ row }) => (
          <Link to={`/enquiry-management/site-visits/${row.original.id}`} className="text-decoration-none link">
            <CodeCell code={row.original.code} />
          </Link>
        ),
      },
      {
        header: "Enquiry",
        accessorKey: "enquiryId",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const enquiry = row.original.enquiry;
          if (!enquiry?.id) {
            return <span className="text-muted">—</span>;
          }
          return (
            <Link
              to={`/enquiry-management/enquiries/${enquiry.id}`}
              className="fw-medium text-decoration-none"
            >
              {enquiry.code ?? `Enquiry #${enquiry.id}`}
            </Link>
          );
        },
      },
      {
        header: "Priority",
        accessorKey: "priority",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const p = row.original.priority;
          if (!p) {
            return <span className="text-muted">—</span>;
          }
          return <StatusBadge value={p} map={EnquirySiteVisitPriorityMap} />;
        },
      },
      {
        header: "Assigned",
        id: "assignedUsers",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const record = row.original;
          const canAssignMore =
            shouldShowSiteVisitActionButtons(record.status) &&
            canAssignSiteVisit;
          return (
            <SiteVisitAssigneesAvatarGroup
              siteVisit={record}
              assignments={record.assignments}
              siteVisitId={record.id}
              canReadDetail={canReadSiteVisit}
              canAssignMore={canAssignMore}
              onOpenAssign={openAssign}
            />
          );
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        cell: ({ row }) => (
          <StatusBadge
            value={row.original.status}
            map={EnquirySiteVisitStatusMap}
          />
        ),
      },
      {
        header: "Created",
        accessorKey: "created_at",
        enableColumnFilter: false,
        cell: ({ row }) =>
          row.original.created_at ? (
            <DateTime date={row.original.created_at} />
          ) : (
            "—"
          ),
      },
      {
        header: "Actions",
        id: "actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <SiteVisitListRowActionsCell
            record={row.original}
            isEmployee={isEmployee}
            canAssignSiteVisit={canAssignSiteVisit}
            canReassignSiteVisit={canReassignSiteVisit}
            busyVisitId={busyVisitId}
            onStartVisit={handleStartVisit}
            onOpenComplete={openCompleteModal}
            onOpenCancel={openCancelModal}
            onOpenAssign={openAssign}
            onOpenReassign={openReassign}
            onDelete={onClickDelete}
          />
        ),
      },
    ],
    [
      busyVisitId,
      canAssignSiteVisit,
      canReassignSiteVisit,
      canReadSiteVisit,
      handleStartVisit,
      isEmployee,
      openAssign,
      openCancelModal,
      openCompleteModal,
      openReassign,
      onClickDelete,
    ],
  );

  return (
    <React.Fragment>
      <EnquirySiteVisitWorkflowModals
        visitId={workflowModalVisit?.id ?? null}
        completeOpen={completeOpen}
        cancelOpen={cancelOpen}
        onCloseComplete={closeCompleteModal}
        onCloseCancel={closeCancelModal}
        onSuccess={refreshTable}
      />

      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteEnquirySiteVisit}
        onCloseClick={() => setDeleteModal(false)}
      />

      <WorkAssignmentForm
        show={assignFormOpen && assignFormVisit != null}
        sourceType={WorkAssignmentSourceType.SITE_VISIT}
        sourceId={assignFormVisit?.id ?? 0}
        sourceLabel={
          assignFormVisit
            ? assignFormVisit.code
              ? `Site visit ${assignFormVisit.code}`
              : `Site visit #${assignFormVisit.id}`
            : undefined
        }
        isReassign={assignFormIsReassign}
        onCloseClick={closeAssignmentForm}
        onSuccess={refreshTable}
      />

      <TableContainer
        columns={columns}
        fetchApi={getEnquirySiteVisits}
        requestParams={requestParams}
        pageSize={10}
        globalSearch={true}
        SearchPlaceholder="Search code, location, enquiry…"
        refreshRef={tableRef}
        divClass="table-responsive table-card mb-3"
        tableClass="align-middle table-nowrap mb-0"
        theadClass="table-light text-muted"
        toolbarRight={<EnquirySiteVisitListFilters
          selectedStatuses={selectedStatuses}
          onToggleStatus={toggleStatus}
          enquiryId={enquiryIdFilter}
          onEnquiryIdChange={setEnquiryIdFilter}
          clientId={clientIdFilter}
          onClientIdChange={setClientIdFilter}
        />}
        filterCount={filtersBadgeCount}
        onFiltersClear={clearListFilters}
      />
    </React.Fragment>
  );
};
